import type { ReactNode } from 'react';
import {
  type IconWeight,
  AirplaneTakeoffIcon,
  BedIcon,
  BusIcon,
  CarIcon,
  CarProfileIcon,
  CheckIcon,
  ClockIcon,
  XIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { t } from './i18n.js';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import type { ModalKind, VoucherBadgeVariant } from './derive.js';

/**
 * O glifo de cada variante do badge — medido no protótipo V2 (2026-07-30), onde o
 * `MarcaDeStatus` troca o ponto por Check / Clock / X segundo o TOM: feito, esperando,
 * morto. O ponto sobrevive nas variantes que o protótipo não nomeia (`gray`, o neutro),
 * porque inventar um glifo para elas seria decidir desenho no lugar do designer.
 */
const BADGE_ICON: Partial<Record<VoucherBadgeVariant, () => ReactNode>> = {
  confirmed: () => <CheckIcon size={12} weight="bold" aria-hidden />,
  progress: () => <ClockIcon size={12} aria-hidden />,
  amber: () => <ClockIcon size={12} aria-hidden />,
  cancelled: () => <XIcon size={12} aria-hidden />,
};

/** Ícone quando o protótipo nomeia um; ponto quando não. */
function BadgeMark({ variant }: { variant: VoucherBadgeVariant }) {
  const icon = BADGE_ICON[variant];
  return icon ? <>{icon()}</> : <span aria-hidden className="size-1.5 rounded-full bg-current" />;
}

/** O tom de cada variante no quarteto semântico do `Badge` do DS. O `progress` (o lilás da
 *  IA no `.vj-badge`) não tem par no quarteto: vira `info`. */
const BADGE_COLOR = {
  progress: 'info',
  confirmed: 'success',
  cancelled: 'danger',
  gray: 'neutral',
  amber: 'warning',
} as const satisfies Record<VoucherBadgeVariant, string>;

const MODAL_ICON: Record<ModalKind, PhosphorIcon> = {
  FLIGHT: AirplaneTakeoffIcon,
  HOTEL: BedIcon,
  BUS: BusIcon,
  RENTAL: CarIcon,
  MOBILITY: CarProfileIcon,
};
interface ModalIconProps {
  modal: ModalKind;
  size?: number;
  /** O peso do glifo — o `bold` das faixas e da calha da página (#6313/#6315). */
  weight?: IconWeight;
}
export function ModalIcon({ modal, size = 19, weight }: Readonly<ModalIconProps>) {
  const Icon = MODAL_ICON[modal];
  return <Icon size={size} weight={weight} aria-hidden />;
}

interface VoucherStatusBadgeProps {
  variant: VoucherBadgeVariant;
  labelKey: string;
}
/** Pill de status do voucher na agenda: o `Badge` soft do DS no tom da variante. */
export function VoucherStatusBadge({ variant, labelKey }: Readonly<VoucherStatusBadgeProps>) {
  return (
    <Badge variant="soft" color={BADGE_COLOR[variant]}>
      <BadgeMark variant={variant} />
      {t(labelKey)}
    </Badge>
  );
}
